import React from "react";
import {
  ActivityIndicator,
  Pressable,
  Text,
  TextInput,
  View,
} from "react-native";

import type { TGetCommunicationsUsageResponse } from "../../../../../services/api-communications";
import { ADMIN_TOKENS } from "../../../../../styles/adminTokens";
import { styles } from "../HomeConversations.styles";

type UsageBillingCardProps = {
  isUsageTab: boolean;
  usageStartDate: string;
  onUsageStartDateChange: (value: string) => void;
  usageEndDate: string;
  onUsageEndDateChange: (value: string) => void;
  usageLoading: boolean;
  usageError: string | null;
  usageSummary: TGetCommunicationsUsageResponse | null;
  usagePeriodStartLabel: string;
  usagePeriodEndLabel: string;
  totalBillableMinutes: number;
  includedMinutesCount: number;
  overageMinutes: number;
  usageProgressPercent: number;
  usageOverageActive: boolean;
  onApplyUsageDate: () => void;
  formatInteger: (value?: number | null) => string;
};

const UsageBillingCard: React.FC<UsageBillingCardProps> = ({
  isUsageTab,
  usageStartDate,
  onUsageStartDateChange,
  usageEndDate,
  onUsageEndDateChange,
  usageLoading,
  usageError,
  usageSummary,
  usagePeriodStartLabel,
  usagePeriodEndLabel,
  totalBillableMinutes,
  includedMinutesCount,
  overageMinutes,
  usageProgressPercent,
  usageOverageActive,
  onApplyUsageDate,
  formatInteger,
}) => {
  return (
    <View style={[styles.card, !isUsageTab && styles.hiddenSection]}>
      <Text style={styles.cardTitle}>Usage & Billing</Text>
      <View style={styles.usageDateRow}>
        <TextInput
          value={usageStartDate}
          onChangeText={onUsageStartDateChange}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={ADMIN_TOKENS.color.textMuted}
          style={[styles.input, styles.usageDateInput]}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Usage start date"
        />
        <TextInput
          value={usageEndDate}
          onChangeText={onUsageEndDateChange}
          placeholder="YYYY-MM-DD"
          placeholderTextColor={ADMIN_TOKENS.color.textMuted}
          style={[styles.input, styles.usageDateInput]}
          autoCapitalize="none"
          autoCorrect={false}
          accessibilityLabel="Usage end date"
        />
        <Pressable
          style={[
            styles.assignModalSecondaryButton,
            usageLoading && styles.callAgainButtonDisabled,
          ]}
          onPress={onApplyUsageDate}
          disabled={usageLoading}
          accessibilityLabel="Apply usage date range"
        >
          <Text style={styles.assignModalSecondaryButtonText}>Apply</Text>
        </Pressable>
      </View>

      {usageLoading ? (
        <View style={styles.loadingInlineRow}>
          <ActivityIndicator size="small" color={ADMIN_TOKENS.color.accent} />
          <Text style={styles.loadingText}>Loading usage summary...</Text>
        </View>
      ) : usageError ? (
        <>
          <Text style={styles.inlineErrorText}>{usageError}</Text>
          <Pressable
            style={styles.callAgainButton}
            onPress={onApplyUsageDate}
            accessibilityLabel="Retry usage summary"
          >
            <Text style={styles.callAgainButtonText}>Retry</Text>
          </Pressable>
        </>
      ) : usageSummary ? (
        <View style={styles.sectionStack}>
          <Text style={styles.metaText}>
            Period: {usagePeriodStartLabel} to {usagePeriodEndLabel}
          </Text>
          <Text style={styles.metaText}>
            Total calls: {formatInteger(usageSummary.total_calls)}
          </Text>
          <Text style={styles.metaText}>
            Completed calls: {formatInteger(usageSummary.completed_calls)}
          </Text>
          <Text style={styles.metaText}>
            Billable minutes: {formatInteger(totalBillableMinutes)}
          </Text>
          <Text style={styles.metaText}>
            Included minutes: {formatInteger(includedMinutesCount)}
          </Text>
          <Text
            style={
              usageOverageActive ? styles.inlineErrorText : styles.metaText
            }
          >
            Overage minutes: {formatInteger(overageMinutes)}
          </Text>
          <View style={styles.usageProgressTrack}>
            <View
              style={[
                styles.usageProgressFill,
                {
                  width: `${usageProgressPercent}%`,
                },
                usageOverageActive && styles.usageProgressFillOverage,
              ]}
            />
          </View>
          <Text style={styles.metaText}>
            {formatInteger(totalBillableMinutes)} /{" "}
            {formatInteger(includedMinutesCount)} included minutes used in this
            period.
          </Text>
          {usageOverageActive ? (
            <View style={styles.overageWarningCard}>
              <Text style={styles.inlineErrorText}>
                Overage is active for the selected period. Additional minutes
                will be billed at the configured overage rate.
              </Text>
            </View>
          ) : null}
        </View>
      ) : (
        <Text style={styles.emptyText}>
          Usage summary unavailable for this period.
        </Text>
      )}
    </View>
  );
};

export default UsageBillingCard;
